import { memoize } from "oj-utils";
import { hasResults } from "../../state/data";
import { State } from "../../state/state";
export const parseOption = memoize((option) => {
    if (typeof option === "string") {
        const [_base, ..._props] = option.split(":");
        const [base, ...modifiers] = _base.split("!");
        const properties = {};
        for (const v of _props ?? []) {
            const [_name, __value] = v.split("=");
            const [_value, ...modifiers] = __value.split("!");
            const name = _name;
            properties[name] = {
                value: _value,
                modifiers: new Set(modifiers ?? [])
            };
        }
        return {
            key: option,
            option: base,
            properties,
            modifiers: new Set(modifiers ?? [])
        };
    }
    return { key: option, option, properties: {}, modifiers: new Set() };
});
export const transformOptions = memoize((options) => {
    const _options = {};
    for (const key of options) {
        const parsed = parseOption(key);
        if (!_options[parsed.option])
            _options[parsed.option] = [];
        _options[parsed.option].push(parsed);
    }
    return Object.entries(_options).map(([option, variants]) => ({
        option,
        variants
    }));
}, options => options.toSorted((a, b) => a.toString().localeCompare(b.toString())).join("|"));
const findValidVariant = (options) => {
    let valid;
    outer: for (const option of options)
        for (const variant of option.variants)
            if (!variant.modifiers.has("NOT_POSSIBLE") && !variant.modifiers.has("DISABLED")) {
                valid = variant;
                break outer;
            }
    return valid;
};
export const validateTransformedOptions = (tab, options, state) => {
    if (tab.key === "type")
        return;
    if (tab.key === "wisselLigger") {
        let shouldUpdate = false;
        for (const option of options) {
            for (const variant of option.variants) {
                const r = hasResults(state.wissel.type.value, state.wissel.hoekVerhouding.value, variant.key, undefined, undefined);
                if (r)
                    variant.modifiers.delete("NOT_POSSIBLE");
                else {
                    variant.modifiers.add("NOT_POSSIBLE");
                    shouldUpdate ||= state.wissel.wisselLigger.value === variant.key;
                }
            }
        }
        if (shouldUpdate)
            state.wissel.wisselLigger.update(findValidVariant(options)?.key);
        return;
    }
    if (tab.key === "hoekVerhouding") {
        let shouldUpdate = false;
        for (const option of options) {
            for (const variant of option.variants) {
                const r = hasResults(state.wissel.type.value, variant.key, state.wissel.wisselLigger.value, undefined, undefined);
                if (r)
                    variant.modifiers.delete("NOT_POSSIBLE");
                else {
                    variant.modifiers.add("NOT_POSSIBLE");
                    shouldUpdate ||= state.wissel.hoekVerhouding.value === variant.key;
                }
            }
        }
        if (shouldUpdate)
            state.wissel.hoekVerhouding.update(findValidVariant(options)?.key);
        return;
    }
    if (tab.key === "spoorStaafProfiel") {
        let shouldUpdate = false;
        for (const option of options) {
            for (const variant of option.variants) {
                const r = hasResults(state.wissel.type.value, state.wissel.hoekVerhouding.value, state.wissel.wisselLigger.value, variant.key, undefined);
                if (r)
                    variant.modifiers.delete("NOT_POSSIBLE");
                else {
                    variant.modifiers.add("NOT_POSSIBLE");
                    shouldUpdate ||= state.wissel.spoorStaafProfiel.value === variant.key;
                }
            }
        }
        if (shouldUpdate)
            state.wissel.spoorStaafProfiel.update(findValidVariant(options)?.key);
        return;
    }
    if (tab.key === "boogStraal") {
        let shouldUpdate = false;
        for (const option of options) {
            for (const variant of option.variants) {
                const r = hasResults(state.wissel.type.value, state.wissel.hoekVerhouding.value, state.wissel.wisselLigger.value, state.wissel.spoorStaafProfiel.value, variant.key);
                if (r)
                    variant.modifiers.delete("NOT_POSSIBLE");
                else {
                    variant.modifiers.add("NOT_POSSIBLE");
                    shouldUpdate ||= state.wissel.boogStraal.value === variant.key;
                }
            }
        }
        if (shouldUpdate)
            state.wissel.boogStraal.update(findValidVariant(options)?.key);
        return;
    }
};
export const findTransformedOptionsNotPossibleCause = (tab, variant, state, allTabs) => {
    if (!variant)
        return;
    if (!variant.modifiers?.has("NOT_POSSIBLE"))
        return;
    const getValidOptionsFor = (tabKey, check) => {
        const targetTab = allTabs.find(t => t.key === tabKey);
        if (!targetTab)
            return [];
        const groups = new Map();
        for (const opt of targetTab.options) {
            const parsed = parseOption(opt);
            const base = String(parsed.option);
            if (!groups.has(base))
                groups.set(base, []);
            groups.get(base).push(parsed.key);
        }
        const result = [];
        for (const [base, keys] of groups) {
            if (keys.some(k => check(k)))
                result.push(base);
        }
        return result;
    };
    if (tab.key === "hoekVerhouding") {
        const values = getValidOptionsFor("type", typeOpt => hasResults(typeOpt, variant.key, state.wissel.wisselLigger.value, undefined, undefined));
        return { key: "type", values };
    }
    if (tab.key === "spoorStaafProfiel") {
        if (hasResults(state.wissel.type.value, undefined, undefined, variant.key, undefined)) {
            const values = getValidOptionsFor("hoekVerhouding", hoekOpt => hasResults(state.wissel.type.value, hoekOpt, undefined, variant.key, undefined));
            return { key: "hoekVerhouding", values };
        }
        if (hasResults(undefined, undefined, undefined, variant.key, undefined)) {
            const values = getValidOptionsFor("type", typeOpt => hasResults(typeOpt, undefined, undefined, variant.key, undefined));
            return { key: "type", values };
        }
    }
    if (tab.key === "boogStraal") {
        if (hasResults(state.wissel.type.value, state.wissel.hoekVerhouding.value, undefined, undefined, variant.key)) {
            const values = getValidOptionsFor("spoorStaafProfiel", profielOpt => hasResults(state.wissel.type.value, state.wissel.hoekVerhouding.value, undefined, profielOpt, variant.key));
            return { key: "spoorStaafProfiel", values };
        }
        if (hasResults(state.wissel.type.value, undefined, undefined, undefined, variant.key)) {
            const values = getValidOptionsFor("hoekVerhouding", hoekOpt => hasResults(state.wissel.type.value, hoekOpt, undefined, undefined, variant.key));
            return { key: "hoekVerhouding", values };
        }
        if (hasResults(undefined, undefined, undefined, undefined, variant.key)) {
            const values = getValidOptionsFor("type", typeOpt => hasResults(typeOpt, undefined, undefined, undefined, variant.key));
            return { key: "type", values };
        }
    }
};
